import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, getRouteApi } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { api } from "../../../lib/api";
import { toastError } from "../../../components/shared/toast";
import { Markdown } from "../../../components/wiki/Markdown";

export const Route = createFileRoute("/_authed/reports/conversations")({
  component: ConversationReportsPage,
});

const authedApi = getRouteApi("/_authed");

type Report = {
  id: string;
  source: "agent" | "json";
  agent_id: string | null;
  client_name: string;
  period_start: string | null;
  period_end: string | null;
  kpis: string | null;
  provider: string;
  model: string;
  status: "queued" | "running" | "done" | "error";
  error: string | null;
  leads_total: number;
  leads_analyzed: number;
  tokens_in: number;
  tokens_out: number;
  cost_usd: number;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  finished_at: string | null;
};

type ReportDetail = Report & { prompt: string; result_md: string | null };

type ListOut = {
  reports: Report[];
  default_prompt: string;
  agents: { id: string; name: string }[];
};

const STATUS_META: Record<Report["status"], { label: string; color: string }> = {
  queued: { label: "Na fila", color: "var(--warn)" },
  running: { label: "Analisando…", color: "var(--warn)" },
  done: { label: "Pronto", color: "var(--ok)" },
  error: { label: "Erro", color: "var(--danger)" },
};

const fmtDate = (s: string | null) =>
  s ? new Date(s).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "—";
const fmtDateTime = (s: string | null) =>
  s ? new Date(s).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
const fmtCost = (v: number) => (v > 0 ? `$${v.toFixed(4)}` : "—");

const inputStyle: React.CSSProperties = {
  background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 6,
  padding: "7px 10px", fontSize: 13, color: "var(--ink)", width: "100%",
};
const btnPrimary: React.CSSProperties = {
  padding: "7px 16px", borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: "pointer",
  background: "var(--brand)", color: "#fff", border: "none",
};
const btnGhost: React.CSSProperties = {
  padding: "7px 14px", borderRadius: 6, fontSize: 13, cursor: "pointer",
  background: "var(--surface-input)", color: "var(--ink-mid)", border: "1px solid var(--line-strong)",
};

function ConversationReportsPage() {
  const { user } = (authedApi.useRouteContext() ?? {}) as { user?: import("../../../lib/auth").CurrentUser };
  const canCreate = user?.role_name === "Admin" || !!user?.permissions?.relatorios;
  const qc = useQueryClient();

  const { data, isLoading } = useQuery<ListOut>({
    queryKey: ["conversation-reports"],
    queryFn: () => api.get("/reports/conversation-reports"),
    refetchInterval: (q) =>
      q.state.data?.reports.some((r) => r.status === "queued" || r.status === "running") ? 3000 : false,
  });

  const [openCard, setOpenCard] = useState<string | null>(null);
  const [formCtx, setFormCtx] = useState<null | { agentId?: string; clientName?: string }>(null);
  const [viewId, setViewId] = useState<string | null>(null);

  // Card = cliente (agrupado pelo nome). A fonte (agente/JSON) é de cada
  // relatório; o agentId mais recente do card só pré-preenche o form.
  const cards = useMemo(() => {
    const map = new Map<string, { key: string; name: string; agentId: string | null; reports: Report[] }>();
    for (const r of data?.reports ?? []) {
      const key = r.client_name;
      const cur = map.get(key) ?? { key, name: r.client_name, agentId: null, reports: [] };
      cur.reports.push(r);
      cur.agentId = cur.agentId ?? r.agent_id;
      map.set(key, cur);
    }
    return [...map.values()];
  }, [data?.reports]);

  const delMut = useMutation({
    mutationFn: (id: string) => api.delete(`/reports/conversation-reports/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["conversation-reports"] }),
    onError: (err) => toastError((err as any)?.payload?.detail ?? "Falha ao excluir."),
  });

  if (isLoading) return <div style={{ color: "var(--ink-muted)", fontSize: 13 }}>Carregando…</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 1100 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Análises de conversas</div>
          <div style={{ fontSize: 12, color: "var(--ink-muted)", marginTop: 2 }}>
            Relatórios gerados por IA sobre as conversas dos leads — por agente ou a partir de um JSON externo.
          </div>
        </div>
        {canCreate && (
          <button style={btnPrimary} onClick={() => setFormCtx({})}>+ Novo relatório</button>
        )}
      </div>

      {cards.length === 0 && (
        <div style={{ color: "var(--ink-muted)", fontSize: 13, padding: "32px 0", textAlign: "center" }}>
          Nenhum relatório ainda. {canCreate ? "Clique em “Novo relatório” para gerar o primeiro." : ""}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 12 }}>
        {cards.map((c) => {
          const running = c.reports.some((r) => r.status === "queued" || r.status === "running");
          const last = c.reports[0];
          const open = openCard === c.key;
          return (
            <div key={c.key}
              style={{
                background: "var(--surface-hover)", border: `1px solid ${open ? "var(--violet-alt)" : "var(--line)"}`,
                borderRadius: 8, padding: "14px 16px", cursor: "pointer",
                gridColumn: open ? "1 / -1" : undefined,
              }}
              onClick={() => setOpenCard(open ? null : c.key)}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</span>
                  {running && <span style={{ fontSize: 11, color: "var(--warn)" }}>● analisando…</span>}
                </div>
                <span style={{ fontSize: 12, color: "var(--ink-muted)", flexShrink: 0 }}>
                  {c.reports.length} relatório{c.reports.length > 1 ? "s" : ""} · {fmtDate(last?.created_at)}
                </span>
              </div>

              {open && (
                <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 12, cursor: "default" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                    <thead>
                      <tr style={{ color: "var(--ink-muted)", textAlign: "left" }}>
                        <th style={{ padding: "6px 8px", fontWeight: 500 }}>Gerado em</th>
                        <th style={{ padding: "6px 8px", fontWeight: 500 }}>Período</th>
                        <th style={{ padding: "6px 8px", fontWeight: 500 }}>Por</th>
                        <th style={{ padding: "6px 8px", fontWeight: 500 }}>Leads</th>
                        <th style={{ padding: "6px 8px", fontWeight: 500 }}>Custo</th>
                        <th style={{ padding: "6px 8px", fontWeight: 500 }}>Status</th>
                        <th style={{ padding: "6px 8px" }} />
                      </tr>
                    </thead>
                    <tbody>
                      {c.reports.map((r) => (
                        <tr key={r.id} style={{ borderTop: "1px solid var(--line)" }}>
                          <td style={{ padding: "7px 8px" }}>{fmtDateTime(r.created_at)}</td>
                          <td style={{ padding: "7px 8px" }}>
                            {r.source === "json" ? (
                              <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: "var(--surface-input)", color: "var(--ink-muted)", border: "1px solid var(--line)" }}>JSON externo</span>
                            ) : (
                              `${fmtDate(r.period_start)} → ${fmtDate(r.period_end)}`
                            )}
                          </td>
                          <td style={{ padding: "7px 8px" }}>{r.created_by_name ?? "—"}</td>
                          <td style={{ padding: "7px 8px" }}>
                            {r.status === "done" ? `${r.leads_analyzed}${r.leads_total > r.leads_analyzed ? `/${r.leads_total}` : ""}` : "—"}
                          </td>
                          <td style={{ padding: "7px 8px" }}>{fmtCost(r.cost_usd)}</td>
                          <td style={{ padding: "7px 8px" }}>
                            <span
                              title={r.error ?? undefined}
                              style={{
                                color: STATUS_META[r.status].color, fontWeight: 500,
                                ...(r.status === "error" ? { fontSize: 11, padding: "2px 7px", borderRadius: 4, border: "1px solid var(--danger)", cursor: "help" } : {}),
                              }}
                            >
                              {STATUS_META[r.status].label}
                            </span>
                          </td>
                          <td style={{ padding: "7px 8px", textAlign: "right", whiteSpace: "nowrap" }}>
                            {(r.status === "done" || r.status === "error") && (
                              <button style={{ ...btnGhost, padding: "3px 10px", fontSize: 12 }} onClick={() => setViewId(r.id)}>Ver</button>
                            )}
                            {(user?.role_name === "Admin" || r.created_by === user?.user_id) && (
                              <button
                                style={{ background: "none", border: "none", color: "var(--ink-muted)", cursor: "pointer", fontSize: 12, marginLeft: 6 }}
                                onClick={() => { if (confirm("Excluir este relatório?")) delMut.mutate(r.id); }}
                              >✕</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {canCreate && (
                    <button
                      style={{ ...btnGhost, marginTop: 10 }}
                      onClick={() => setFormCtx({ clientName: c.name, agentId: c.agentId ?? undefined })}
                    >+ Novo relatório para {c.name}</button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {formCtx && data && (
        <NewReportModal
          ctx={formCtx}
          agents={data.agents}
          defaultPrompt={data.default_prompt}
          onClose={() => setFormCtx(null)}
          onCreated={() => { setFormCtx(null); qc.invalidateQueries({ queryKey: ["conversation-reports"] }); }}
        />
      )}
      {viewId && <ReportViewModal reportId={viewId} onClose={() => setViewId(null)} />}
    </div>
  );
}

// ── form de novo relatório ───────────────────────────────────────────────────

function NewReportModal({ ctx, agents, defaultPrompt, onClose, onCreated }: {
  ctx: { agentId?: string; clientName?: string };
  agents: { id: string; name: string }[];
  defaultPrompt: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [source, setSource] = useState<"agent" | "json">(
    ctx.agentId ? "agent" : ctx.clientName ? "json" : "agent",
  );
  const [agentId, setAgentId] = useState(ctx.agentId ?? "");
  const [clientName, setClientName] = useState(ctx.clientName ?? "");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [payload, setPayload] = useState<string | null>(null);
  const [fileName, setFileName] = useState("");
  const [prompt, setPrompt] = useState(defaultPrompt);
  const [kpis, setKpis] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  // Dentro de um card só o nome do cliente trava; a fonte é livre por relatório.
  const lockedName = !!ctx.clientName;

  const createMut = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post("/reports/conversation-reports", body, 60_000),
    onSuccess: onCreated,
    onError: (err) => toastError((err as any)?.payload?.detail ?? "Falha ao criar o relatório."),
  });

  const submit = () => {
    if (source === "agent") {
      if (!agentId || !fromDate || !toDate) { toastError("Selecione o agente e o período."); return; }
      createMut.mutate({
        source, agent_id: agentId, prompt, kpis: kpis || null,
        client_name: ctx.clientName ?? null,
        period_start: new Date(fromDate).toISOString(),
        period_end: new Date(toDate + "T23:59:59").toISOString(),
      });
    } else {
      if (!clientName.trim()) { toastError("Informe o nome do cliente."); return; }
      if (!payload) { toastError("Envie o arquivo JSON exportado."); return; }
      createMut.mutate({ source, client_name: clientName.trim(), source_payload: payload, prompt, kpis: kpis || null });
    }
  };

  const onFiles = async (list: FileList | null) => {
    const files = [...(list ?? [])];
    if (!files.length) return;
    if (files.reduce((s, f) => s + f.size, 0) > 5 * 1024 * 1024) {
      toastError("Arquivos somam mais de 5 MB."); return;
    }
    try {
      const parsed = await Promise.all(files.map(async (f) => {
        try { return JSON.parse(await f.text()); }
        catch { throw new Error(f.name); }
      }));
      // Vários arquivos: arrays são concatenados; outros valores entram como itens.
      const merged = parsed.length === 1 ? parsed[0] : parsed.flatMap((p) => (Array.isArray(p) ? p : [p]));
      setPayload(JSON.stringify(merged));
      setFileName(files.length === 1 ? files[0].name : `${files.length} arquivos`);
    } catch (e) {
      toastError(`Arquivo não é um JSON válido: ${(e as Error).message}`);
    }
  };

  return (
    <Backdrop onClose={onClose}>
      <div style={{ width: "min(640px, 92vw)", maxHeight: "88vh", overflowY: "auto", background: "var(--surface-raised)", border: "1px solid var(--line)", borderRadius: 10, padding: 20 }}
        onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Novo relatório de análise</div>

        {lockedName && (
          <div style={{ fontSize: 12, color: "var(--ink-muted)", marginBottom: 12 }}>
            Cliente: <span style={{ color: "var(--ink)", fontWeight: 600 }}>{ctx.clientName}</span>
          </div>
        )}
        {(
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            {(["agent", "json"] as const).map((s) => (
              <button key={s} onClick={() => setSource(s)}
                style={{
                  ...btnGhost, flex: 1,
                  border: `1px solid ${source === s ? "var(--violet-alt)" : "var(--line-strong)"}`,
                  color: source === s ? "var(--violet)" : "var(--ink-mid)",
                  background: source === s ? "rgba(108,71,255,0.15)" : "var(--surface-input)",
                }}>
                {s === "agent" ? "Agente do sistema" : "JSON externo (legacy)"}
              </button>
            ))}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {source === "agent" ? (
            <>
              <label style={{ fontSize: 12, color: "var(--ink-muted)" }}>
                Agente
                <select value={agentId} onChange={(e) => setAgentId(e.target.value)}
                  style={{ ...inputStyle, marginTop: 4 }}>
                  <option value="">Selecione…</option>
                  {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </label>
              <div style={{ display: "flex", gap: 10 }}>
                <label style={{ fontSize: 12, color: "var(--ink-muted)", flex: 1 }}>
                  De
                  <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ ...inputStyle, marginTop: 4 }} />
                </label>
                <label style={{ fontSize: 12, color: "var(--ink-muted)", flex: 1 }}>
                  Até
                  <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ ...inputStyle, marginTop: 4 }} />
                </label>
              </div>
              <div style={{ fontSize: 11.5, color: "var(--ink-muted)" }}>
                Entram todos os leads que trocaram mensagens no período — a conversa inteira de cada um é analisada.
              </div>
            </>
          ) : (
            <>
              {!lockedName && (
                <label style={{ fontSize: 12, color: "var(--ink-muted)" }}>
                  Nome do cliente
                  <input value={clientName} onChange={(e) => setClientName(e.target.value)}
                    placeholder="Ex.: Revenda XYZ" style={{ ...inputStyle, marginTop: 4 }} />
                </label>
              )}
              <label style={{ fontSize: 12, color: "var(--ink-muted)" }}>
                Arquivos JSON (export do sistema — pode selecionar vários)
                <div style={{ marginTop: 4 }}>
                  <input ref={fileRef} type="file" accept=".json,application/json" multiple style={{ display: "none" }}
                    onChange={(e) => onFiles(e.target.files)} />
                  <button style={btnGhost} onClick={() => fileRef.current?.click()}>
                    {fileName ? `📄 ${fileName}` : "Escolher arquivos…"}
                  </button>
                </div>
              </label>
            </>
          )}

          <label style={{ fontSize: 12, color: "var(--ink-muted)" }}>
            Prompt de análise
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={8}
              style={{ ...inputStyle, marginTop: 4, resize: "vertical", fontFamily: "inherit", lineHeight: 1.5 }} />
          </label>

          <label style={{ fontSize: 12, color: "var(--ink-muted)" }}>
            KPIs que você busca (opcional)
            <textarea value={kpis} onChange={(e) => setKpis(e.target.value)} rows={3}
              placeholder="Ex.: taxa de leads qualificados, carros mais procurados, motivo das perdas…"
              style={{ ...inputStyle, marginTop: 4, resize: "vertical", fontFamily: "inherit", lineHeight: 1.5 }} />
          </label>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button style={btnGhost} onClick={onClose}>Cancelar</button>
          <button style={{ ...btnPrimary, opacity: createMut.isPending ? 0.6 : 1 }} disabled={createMut.isPending} onClick={submit}>
            {createMut.isPending ? "Enviando…" : "Gerar relatório"}
          </button>
        </div>
      </div>
    </Backdrop>
  );
}

// ── visualização do relatório ────────────────────────────────────────────────

function ReportViewModal({ reportId, onClose }: { reportId: string; onClose: () => void }) {
  const { data } = useQuery<ReportDetail>({
    queryKey: ["conversation-report", reportId],
    queryFn: () => api.get(`/reports/conversation-reports/${reportId}`),
  });

  const exportPdf = () => {
    if (!data?.result_md) return;
    const html = DOMPurify.sanitize(marked.parse(data.result_md, { async: false, gfm: true, breaks: true }) as string);
    const period = data.period_start ? `${fmtDate(data.period_start)} a ${fmtDate(data.period_end)}` : "JSON externo";
    // Nome do agente é editável pelo cliente no portal: sem escape, virava XSS
    // na origem do painel ao exportar (o corpo já passa pelo DOMPurify).
    const esc = (s: unknown) =>
      String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
    const w = window.open("", "_blank");
    if (!w) { toastError("Popup bloqueado pelo navegador."); return; }
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Relatório — ${esc(data.client_name)}</title>
      <style>
        body { font-family: -apple-system, "Segoe UI", Roboto, sans-serif; color: #1a1a1a; max-width: 800px; margin: 32px auto; padding: 0 24px; line-height: 1.65; font-size: 14px; }
        h1 { font-size: 22px; margin-bottom: 2px; } h2 { font-size: 17px; margin-top: 24px; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
        .meta { color: #666; font-size: 12px; margin-bottom: 24px; }
        table { border-collapse: collapse; } td, th { border: 1px solid #ccc; padding: 4px 8px; font-size: 13px; }
        @media print { body { margin: 0 auto; } }
      </style></head><body>
      <h1>Relatório de análise — ${esc(data.client_name)}</h1>
      <div class="meta">Período: ${esc(period)} · Gerado em ${fmtDateTime(data.created_at)} · ${data.leads_analyzed > 0 ? `${data.leads_analyzed} leads analisados · ` : ""}Modelo: ${esc(data.model)}</div>
      ${html}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  return (
    <Backdrop onClose={onClose}>
      <div style={{ width: "min(820px, 94vw)", maxHeight: "90vh", display: "flex", flexDirection: "column", background: "var(--surface-raised)", border: "1px solid var(--line)", borderRadius: 10 }}
        onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: "1px solid var(--line)", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>{data?.client_name ?? "…"}</div>
            {data && (
              <div style={{ fontSize: 11.5, color: "var(--ink-muted)", marginTop: 2 }}>
                {data.period_start ? `${fmtDate(data.period_start)} → ${fmtDate(data.period_end)} · ` : ""}
                {data.leads_analyzed > 0 ? `${data.leads_analyzed}${data.leads_total > data.leads_analyzed ? ` de ${data.leads_total}` : ""} leads · ` : ""}
                {data.model} · {data.tokens_in + data.tokens_out} tokens · {fmtCost(data.cost_usd)}
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={btnGhost} onClick={exportPdf}>Exportar PDF</button>
            <button style={btnGhost} onClick={onClose}>Fechar</button>
          </div>
        </div>
        <div style={{ overflowY: "auto", padding: "18px 24px" }}>
          {data && (
            <details style={{ marginBottom: 16 }}>
              <summary style={{ fontSize: 12, color: "var(--ink-muted)", cursor: "pointer", userSelect: "none" }}>
                Prompt e KPIs usados nesta análise
              </summary>
              <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                {data.kpis && (
                  <div>
                    <div style={{ fontSize: 11, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4 }}>KPIs solicitadas</div>
                    <pre style={{ whiteSpace: "pre-wrap", fontSize: 12, lineHeight: 1.5, background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 6, padding: "10px 12px", margin: 0, fontFamily: "inherit", color: "var(--ink)" }}>{data.kpis}</pre>
                  </div>
                )}
                <div>
                  <div style={{ fontSize: 11, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4 }}>Prompt de análise</div>
                  <pre style={{ whiteSpace: "pre-wrap", fontSize: 12, lineHeight: 1.5, background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 6, padding: "10px 12px", margin: 0, fontFamily: "inherit", color: "var(--ink)", maxHeight: 260, overflowY: "auto" }}>{data.prompt}</pre>
                </div>
              </div>
            </details>
          )}
          {!data ? (
            <div style={{ color: "var(--ink-muted)", fontSize: 13 }}>Carregando…</div>
          ) : data.result_md ? (
            <Markdown source={data.result_md} />
          ) : (
            <div style={{ color: "var(--danger)", fontSize: 13 }}>
              O modelo não retornou conteúdo neste relatório.
              {data.error ? ` ${data.error}` : " Gere novamente (se o raciocínio estiver ligado no painel Dev, desligue — ele pode consumir todo o limite de saída)."}
            </div>
          )}
        </div>
      </div>
    </Backdrop>
  );
}

// Backdrop que fecha ao clicar fora (padrão do app para toda popup).
function Backdrop({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 80, display: "flex", alignItems: "center", justifyContent: "center" }}>
      {children}
    </div>
  );
}
